import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { generateArticle } from '../../utils/structuredData';
import seoData from '../../config/seoData';
import { MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock, Store } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaComercial = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Vi o artigo sobre limpeza comercial e gostaria de um orçamento.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/blog/limpeza-comercial'].title}
        description={seoData['/blog/limpeza-comercial'].description}
        canonical={seoData['/blog/limpeza-comercial'].canonical}
      /> bg-white">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-blue-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Início</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded text-green-400 font-semibold">Blog</button>
            </div>
          </div>
        )}
      </header>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <button onClick={() => navigate('/blog')} className="flex items-center text-blue-600 hover:text-blue-800 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />Voltar para o Blog
          </button>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Limpeza Pós-Obra Comercial: Como Entregar sua Loja Pronta para Inaugurar
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /><span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" /><span>8 min de leitura</span>
              </div>
            </div>
          </header>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              A <strong>limpeza pós-obra comercial</strong> é crucial para inaugurações de lojas, restaurantes e escritórios. Um ambiente limpo e bem apresentado transmite profissionalismo e atrai clientes desde o primeiro dia.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Por Que a Limpeza Comercial é Diferente?
            </h2>

            <Card className="bg-blue-50 border-2 border-blue-200 mb-6">
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Store className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Prazos apertados:</strong> Inauguração com data marcada</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Store className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Primeira impressão:</strong> Clientes julgam pela aparência</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Store className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Normas sanitárias:</strong> Especialmente para restaurantes e clínicas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Store className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Alto padrão:</strong> Acabamento impecável esperado</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Checklist por Tipo de Estabelecimento
            </h2>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              Lojas de Varejo
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Vitrine e fachada impecáveis</li>
              <li>Piso brilhante sem manchas</li>
              <li>Provadores limpos e cheirosos</li>
              <li>Espelhos sem marcas</li>
              <li>Iluminação e luminosos limpos</li>
              <li>Banheiro para clientes higienizado</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              Restaurantes e Cafés
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Cozinha industrial (normas ANVISA)</li>
              <li>Salão e mesas sem resíduos</li>
              <li>Banheiros seguindo normas sanitárias</li>
              <li>Exaustores e coifas limpos</li>
              <li>Pisos e azulejos sem gordura</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              Escritórios e Clínicas
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Carpetes ou pisos aspirados profundamente</li>
              <li>Vidros e divisórias sem marcas</li>
              <li>Banheiros e copas desinfetados</li>
              <li>Móveis limpos e protegidos</li>
              <li>Ar-condicionado limpo</li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Processo Passo a Passo
            </h2>

            <Card className="bg-green-50 border-2 border-green-200 mb-6">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Dia 1: Limpeza Grossa</h4>
                    <p className="text-gray-700">Remoção de entulho, varredura e aspiração inicial</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Dia 2: Limpeza Técnica</h4>
                    <p className="text-gray-700">Remoção de cimento, tinta, rejunte com produtos específicos</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Dia 3: Lavagem e Polimento</h4>
                    <p className="text-gray-700">Lavagem profissional de pisos, polimento de vidros e metais</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Dia 4: Acabamento e Detalhes</h4>
                    <p className="text-gray-700">Limpeza fina, toques finais, conferência geral</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Tempo e Custo Médio
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-left">Tipo</th>
                    <th className="p-4 text-left">Área Média</th>
                    <th className="p-4 text-left">Tempo</th>
                    <th className="p-4 text-left">Custo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Loja Pequena</td>
                    <td className="p-4">50-100m²</td>
                    <td className="p-4">2-3 dias</td>
                    <td className="p-4">R$ 800-1.500</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Loja Média</td>
                    <td className="p-4">100-300m²</td>
                    <td className="p-4">3-4 dias</td>
                    <td className="p-4">R$ 1.500-3.500</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Restaurante</td>
                    <td className="p-4">150-250m²</td>
                    <td className="p-4">4-5 dias</td>
                    <td className="p-4">R$ 2.500-5.000</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-semibold">Escritório</td>
                    <td className="p-4">200-500m²</td>
                    <td className="p-4">3-5 dias</td>
                    <td className="p-4">R$ 2.000-6.000</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Dicas para Empresários
            </h2>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Contrate a limpeza com 1 semana de antecedência da inauguração</li>
              <li>Finalize TODAS as obras antes da limpeza (evite retrabalho)</li>
              <li>Tire fotos do "antes e depois" para marketing</li>
              <li>Peça certificado de limpeza para restaurantes (exigido pela vigilância sanitária)</li>
              <li>Reserve 1 dia após limpeza para montagem final sem sujeira</li>
            </ul>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Inaugurando seu Estabelecimento?</h3>
                <p className="mb-6">
                  A WellTec garante limpeza profissional para sua inauguração em BH
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Orçamento para Estabelecimento Comercial
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-bold mb-4">WellTec</h3>
              <p className="text-gray-400 text-sm">Limpeza pós-obra profissional em BH</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="tel:+5531971823249" className="hover:text-green-400">(31) 97182-3249</a></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      <button onClick={handleWhatsAppClick} className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50">
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default LimpezaComercial;
